import { createSlice } from '@reduxjs/toolkit';

import { generateRebuzzUIDKey } from './utils/generateRebuzzUIDKey';

import {
  initialItemRebuzzStatistics,
  initialRebuzzSelectedTags,
} from './constants/initialStates';
import {
  REBUZZ_SORT_OPTIONS,
  REBUZZ_SORT_OPTIONS_ORDER,
} from './constants/rebuzzSortOptions';
import { DEFINED_BOOST_TAG } from '../../constants/definedBoostTag';

const INITIAL_STATE = {
  statistics: {
    reactions: {
      byId: {
        // reactionType: [
        //   {
        //     cid: {
        //       orgId: string,
        //       streamId: string,
        //       channelId: string,
        //     },
        //     quantity: number,
        //   },
        // ],
      },
    },
    item: {
      byId: {},
      filters: {
        byId: {},
      },
    },
  },
  selectedTags: {
    // [thread/anchorItemId/streamItemId || streamItemId]: initialRebuzzSelectedTags
  },
  quickRebuzzTags: {
    // [thread/anchorItemId/streamItemId || streamItemId]: rebuzzSelectedTag
  },
  suggestionsChannels: [],
  rebuzzOption: {
    byId: {
      // [thread/anchorItemId/streamItemId || streamItemId]: option
    },
  },
  activeEditorId: {},
  rebuzzSort: {
    sortOption: REBUZZ_SORT_OPTIONS[0],
    sortOrder: REBUZZ_SORT_OPTIONS_ORDER[0],
  },
  isCheckedTag: {
    // [thread/anchorItemId/streamItemId || streamItemId]: {
    //   [channelCIDKey]: boolean
    // },
  },
};

export const RebuzzSlice = createSlice({
  name: 'rebuzz',
  initialState: INITIAL_STATE,
  reducers: {
    addRebuzzTag: (state, { payload }) => {
      const { boost, anchorItemId, streamItemId, tag } = payload;

      const price = tag[boost] || 0;
      const boostTag = DEFINED_BOOST_TAG[boost];
      const rebuzzUIDKey = generateRebuzzUIDKey({ anchorItemId, streamItemId });

      if (!state.selectedTags[rebuzzUIDKey]) {
        state.selectedTags[rebuzzUIDKey] = {
          byId: {},
          byBoost: {},
          totalPrice: 0,
        };
      }

      const selectedTag = state.selectedTags[rebuzzUIDKey];
      const prevTag = selectedTag.byId[tag.id];
      const prevPrice = prevTag ? prevTag.price : 0;
      const prevBoostTag = prevTag ? prevTag.boost : null;

      selectedTag.byId[tag.id] = {
        ...tag,
        boost: boostTag,
        price,
      };

      if (prevBoostTag && prevBoostTag !== boostTag) {
        selectedTag.byBoost[prevBoostTag] = selectedTag.byBoost[
          prevBoostTag
        ].filter(id => id !== tag.id);
      }
      if (!selectedTag.byBoost[boostTag]) {
        selectedTag.byBoost[boostTag] = [];
      }
      if (!selectedTag.byBoost[boostTag].includes(tag.id)) {
        selectedTag.byBoost[boostTag].push(tag.id);
      }

      selectedTag.totalPrice = selectedTag.totalPrice - prevPrice + price;
      state.selectedTags[rebuzzUIDKey] = selectedTag;
    },
    resetQuickRebuzzTag: (state, { payload }) => {
      const { anchorItemId, streamItemId } = payload;
      const rebuzzUIDKey = generateRebuzzUIDKey({ anchorItemId, streamItemId });

      delete state.quickRebuzzTags[rebuzzUIDKey];
    },
    addQuickRebuzzTag: (state, { payload }) => {
      const { anchorItemId, streamItemId, tag } = payload;
      state.quickRebuzzTags[
        generateRebuzzUIDKey({ anchorItemId, streamItemId })
      ] = tag;
    },
    setIsCheckedRebuzzTag: (state, { payload }) => {
      const { anchorItemId, streamItemId, id } = payload;
      const rebuzzUIDKey = generateRebuzzUIDKey({ anchorItemId, streamItemId });

      if (!state.isCheckedTag[rebuzzUIDKey]) {
        state.isCheckedTag[rebuzzUIDKey] = {};
      }

      state.isCheckedTag[rebuzzUIDKey][id] =
        !state.isCheckedTag[rebuzzUIDKey][id];
    },
    resetCheckedRebuzzTags: (state, { payload }) => {
      const { anchorItemId, streamItemId } = payload;
      const rebuzzUIDKey = generateRebuzzUIDKey({ anchorItemId, streamItemId });

      delete state.isCheckedTag[rebuzzUIDKey];
    },
    resetSelectedTags: (state, { payload }) => {
      const { anchorItemId, streamItemId } = payload;
      const rebuzzUIDKey = generateRebuzzUIDKey({ anchorItemId, streamItemId });

      delete state.selectedTags[rebuzzUIDKey];
    },
    removeRebuzzChannelTag: (state, { payload }) => {
      const { id, anchorItemId, streamItemId } = payload;
      const rebuzzUIDKey = generateRebuzzUIDKey({ anchorItemId, streamItemId });
      const tag = state.selectedTags[rebuzzUIDKey].byId[id];

      state.selectedTags[rebuzzUIDKey].byBoost[tag.boost] = state.selectedTags[
        rebuzzUIDKey
      ].byBoost[tag.boost].filter(tagId => tagId !== id);
      state.selectedTags[rebuzzUIDKey].totalPrice -= tag.price;

      delete state.selectedTags[rebuzzUIDKey].byId[id];
    },
    setRebuzzOption: (state, { payload }) => {
      const { anchorItemId, streamItemId, option } = payload;
      const rebuzzUIDKey = generateRebuzzUIDKey({ anchorItemId, streamItemId });

      if (option) {
        state.rebuzzOption.byId[rebuzzUIDKey] = option;
      } else delete state.rebuzzOption.byId[rebuzzUIDKey];
    },
    setActiveRebuzzEditorId: (state, { payload }) => {
      const { id, streamItemId, anchorItemId } = payload;

      state.activeEditorId[
        generateRebuzzUIDKey({ anchorItemId, streamItemId })
      ] = id;
    },
    setRebuzzBoostSum: (state, { payload }) => {
      const { anchorItemId, streamItemId, id, boostAmount } = payload;
      const rebuzzUIDKey = generateRebuzzUIDKey({ anchorItemId, streamItemId });

      state.selectedTags[rebuzzUIDKey].totalPrice =
        state.selectedTags[rebuzzUIDKey].totalPrice -
        state.selectedTags[rebuzzUIDKey].byId[id].price +
        boostAmount;
      state.selectedTags[rebuzzUIDKey].byId[id].price = boostAmount;
    },
    setCurrentSortOption: (state, { payload }) => {
      state.rebuzzSort.sortOption = payload.currentOption;
      state.rebuzzSort.sortOrder = payload.order;
    },
    resetRebuzzSlice: () => INITIAL_STATE,
  },
});

export const selectorGetQuickRebuzzSelectedTag = (
  state,
  { anchorItemId, streamItemId },
) =>
  state.rebuzz.quickRebuzzTags[
    generateRebuzzUIDKey({ anchorItemId, streamItemId })
  ];
export const selectorRebuzzSelectedTags = (
  state,
  { anchorItemId, streamItemId },
) =>
  state.rebuzz.selectedTags[
    generateRebuzzUIDKey({ anchorItemId, streamItemId })
  ] || { ...initialRebuzzSelectedTags };
export const selectorGetRebuzzSelectedTag = (
  state,
  { anchorItemId, streamItemId, channelCIDKey },
) =>
  state.rebuzz.selectedTags[
    generateRebuzzUIDKey({ anchorItemId, streamItemId })
  ]?.byId?.[channelCIDKey];
export const selectorGetItemRebuzzStatistics = (state, channelKey) =>
  state.rebuzz.statistics.item.byId[channelKey] || initialItemRebuzzStatistics;
export const selectorGetItemRebuzzStatisticsFilter = (state, itemCIDKey) =>
  state.rebuzz.statistics.item.filters.byId[itemCIDKey] || [];
export const selectorGetItemStatisticsReactions = (state, itemCidKey) =>
  state.rebuzz.statistics.reactions.byId[itemCidKey] || {};
export const selectorGetActiveRebuzzEditorId = (
  state,
  { anchorItemId, streamItemId },
) =>
  state.rebuzz.activeEditorId[
    generateRebuzzUIDKey({ anchorItemId, streamItemId })
  ];
export const selectorGetSumOfRebuzzBoost = (
  state,
  { anchorItemId, streamItemId },
) =>
  state.rebuzz.selectedTags[
    generateRebuzzUIDKey({ anchorItemId, streamItemId })
  ]?.totalPrice;
export const selectorGetRebuzzOption = (
  state,
  { anchorItemId, streamItemId },
) =>
  state.rebuzz.rebuzzOption.byId[
    generateRebuzzUIDKey({ anchorItemId, streamItemId })
  ];
export const selectorGetSelectedRebuzzTagsAmount = (
  state,
  { anchorItemId, streamItemId },
) => {
  const byBoost =
    state.rebuzz.selectedTags[
      generateRebuzzUIDKey({ anchorItemId, streamItemId })
    ]?.byBoost || {};

  return Object.values(byBoost).flat().length;
};
export const selectorGetSuggestionsChannels = state =>
  state.rebuzz.suggestionsChannels;
export const selectorGetRebuzzSortOptions = state => state.rebuzz.rebuzzSort;
export const selectorGetRebuzzChecks = (
  state,
  { anchorItemId, streamItemId },
) =>
  state.rebuzz.isCheckedTag[
    generateRebuzzUIDKey({ anchorItemId, streamItemId })
  ] || {};
export const selectorGetIsRebuzzTagChecked = (
  state,
  { anchorItemId, streamItemId, id },
) =>
  state.rebuzz.isCheckedTag[
    generateRebuzzUIDKey({ anchorItemId, streamItemId })
  ]?.[id] || false;

export const {
  setActiveRebuzzEditorId,
  addRebuzzTag,
  resetQuickRebuzzTag,
  addQuickRebuzzTag,
  setIsCheckedRebuzzTag,
  resetCheckedRebuzzTags,
  removeRebuzzChannelTag,
  setRebuzzOption,
  setRebuzzBoostSum,
  resetRebuzzSlice,
  setCurrentSortOption,
  resetSelectedTags,
} = RebuzzSlice.actions;

export default RebuzzSlice.reducer;
